/**
 * スナップ機能ユーティリティ
 * 家具の位置・サイズを他の家具や壁に吸着させる
 */

// デフォルトのスナップ閾値（cm）
const POSITION_SNAP_THRESHOLD = 15
const RESIZE_SNAP_THRESHOLD = 10

/**
 * 位置スナップ: ドラッグ移動時に壁・他家具に吸着
 * @param {Object} pos - 現在の位置 {x, z}
 * @param {Object} size - 家具のサイズ {width, depth}
 * @param {number} rotation - 回転角度（度数法）
 * @param {Object} room - 部屋のサイズ {width, depth}
 * @param {Array} otherFurniture - 他の家具リスト
 * @param {number} threshold - スナップ閾値（cm）
 * @returns {Object} {x, z, snappedX, snappedZ, snapLines}
 */
export function snapPosition(pos, size, rotation, room, otherFurniture, threshold = POSITION_SNAP_THRESHOLD) {
    let newX = pos.x
    let newZ = pos.z
    let snappedX = false
    let snappedZ = false
    const snapLines = []

    // 回転を考慮した実効サイズ
    const isRotated90 = Math.abs(rotation % 180) === 90
    const effectiveWidth = isRotated90 ? size.depth : size.width
    const effectiveDepth = isRotated90 ? size.width : size.depth

    const halfWidth = effectiveWidth / 2
    const halfDepth = effectiveDepth / 2
    const roomHalfWidth = room.width / 2
    const roomHalfDepth = room.depth / 2

    // この家具の端
    const myLeft = pos.x - halfWidth
    const myRight = pos.x + halfWidth
    const myFront = pos.z - halfDepth
    const myBack = pos.z + halfDepth

    // === 壁へのスナップ ===
    // 左壁
    if (Math.abs(myLeft - (-roomHalfWidth)) < threshold) {
        newX = -roomHalfWidth + halfWidth
        snappedX = true
        snapLines.push({ type: 'wall', axis: 'x', position: -roomHalfWidth })
    }
    // 右壁
    else if (Math.abs(myRight - roomHalfWidth) < threshold) {
        newX = roomHalfWidth - halfWidth
        snappedX = true
        snapLines.push({ type: 'wall', axis: 'x', position: roomHalfWidth })
    }

    // 前壁
    if (Math.abs(myFront - (-roomHalfDepth)) < threshold) {
        newZ = -roomHalfDepth + halfDepth
        snappedZ = true
        snapLines.push({ type: 'wall', axis: 'z', position: -roomHalfDepth })
    }
    // 後壁
    else if (Math.abs(myBack - roomHalfDepth) < threshold) {
        newZ = roomHalfDepth - halfDepth
        snappedZ = true
        snapLines.push({ type: 'wall', axis: 'z', position: roomHalfDepth })
    }

    // === 他の家具へのスナップ ===
    for (const other of otherFurniture) {
        const otherRotation = other.rotation || 0
        const otherRotated90 = Math.abs(otherRotation % 180) === 90
        const otherWidth = otherRotated90 ? other.size.depth : other.size.width
        const otherDepth = otherRotated90 ? other.size.width : other.size.depth

        const otherLeft = other.position.x - otherWidth / 2
        const otherRight = other.position.x + otherWidth / 2
        const otherCenter = other.position.x
        const otherFront = other.position.z - otherDepth / 2
        const otherBack = other.position.z + otherDepth / 2
        const otherCenterZ = other.position.z

        // X軸スナップ（まだスナップされていない場合）
        if (!snappedX) {
            // 左端 → 他の右端
            if (Math.abs(myLeft - otherRight) < threshold) {
                newX = otherRight + halfWidth
                snappedX = true
                snapLines.push({ type: 'furniture', axis: 'x', position: otherRight })
            }
            // 右端 → 他の左端
            else if (Math.abs(myRight - otherLeft) < threshold) {
                newX = otherLeft - halfWidth
                snappedX = true
                snapLines.push({ type: 'furniture', axis: 'x', position: otherLeft })
            }
            // 左端 → 他の左端（端揃え）
            else if (Math.abs(myLeft - otherLeft) < threshold) {
                newX = otherLeft + halfWidth
                snappedX = true
                snapLines.push({ type: 'align', axis: 'x', position: otherLeft })
            }
            // 右端 → 他の右端（端揃え）
            else if (Math.abs(myRight - otherRight) < threshold) {
                newX = otherRight - halfWidth
                snappedX = true
                snapLines.push({ type: 'align', axis: 'x', position: otherRight })
            }
            // 中心 → 他の中心
            else if (Math.abs(pos.x - otherCenter) < threshold) {
                newX = otherCenter
                snappedX = true
                snapLines.push({ type: 'center', axis: 'x', position: otherCenter })
            }
        }

        // Z軸スナップ（まだスナップされていない場合）
        if (!snappedZ) {
            // 前端 → 他の後端
            if (Math.abs(myFront - otherBack) < threshold) {
                newZ = otherBack + halfDepth
                snappedZ = true
                snapLines.push({ type: 'furniture', axis: 'z', position: otherBack })
            }
            // 後端 → 他の前端
            else if (Math.abs(myBack - otherFront) < threshold) {
                newZ = otherFront - halfDepth
                snappedZ = true
                snapLines.push({ type: 'furniture', axis: 'z', position: otherFront })
            }
            // 前端 → 他の前端（端揃え）
            else if (Math.abs(myFront - otherFront) < threshold) {
                newZ = otherFront + halfDepth
                snappedZ = true
                snapLines.push({ type: 'align', axis: 'z', position: otherFront })
            }
            // 後端 → 他の後端（端揃え）
            else if (Math.abs(myBack - otherBack) < threshold) {
                newZ = otherBack - halfDepth
                snappedZ = true
                snapLines.push({ type: 'align', axis: 'z', position: otherBack })
            }
            // 中心 → 他の中心
            else if (Math.abs(pos.z - otherCenterZ) < threshold) {
                newZ = otherCenterZ
                snappedZ = true
                snapLines.push({ type: 'center', axis: 'z', position: otherCenterZ })
            }
        }
    }

    return { x: newX, z: newZ, snappedX, snappedZ, snapLines }
}

/**
 * リサイズスナップ: サイズ変更時に壁・他家具に追従
 * @param {Object} newSize - 新しいサイズ {width, depth, height}
 * @param {Object} pos - 現在の位置 {x, z}
 * @param {number} rotation - 回転角度（度数法）
 * @param {Object} room - 部屋のサイズ {width, depth, height}
 * @param {Array} otherFurniture - 他の家具リスト
 * @param {string} dimension - 変更中の次元 ('width', 'depth', 'height')
 * @param {number} threshold - スナップ閾値（cm）
 * @returns {Object} {value, snapped, snapLine}
 */
export function snapResize(newSize, pos, rotation, room, otherFurniture, dimension, threshold = RESIZE_SNAP_THRESHOLD) {
    let value = newSize[dimension]
    let snapped = false
    let snapLine = null

    // 回転を考慮
    const isRotated90 = Math.abs(rotation % 180) === 90

    // 高さの場合は部屋の天井に制限
    if (dimension === 'height') {
        if (Math.abs(value - room.height) < threshold) {
            value = room.height
            snapped = true
            snapLine = { type: 'wall', axis: 'y', position: room.height }
        } else if (value > room.height) {
            value = room.height
            snapped = true
        }
        return { value, snapped, snapLine }
    }

    // 幅・奥行きの場合
    const isWidth = dimension === 'width'
    const effectiveDimension = isRotated90 ? (isWidth ? 'depth' : 'width') : dimension

    const roomHalfWidth = room.width / 2
    const roomHalfDepth = room.depth / 2
    const halfValue = value / 2

    if (effectiveDimension === 'width') {
        // 壁への到達チェック（左右）
        const leftEdge = pos.x - halfValue
        const rightEdge = pos.x + halfValue

        if (leftEdge < -roomHalfWidth) {
            value = (pos.x + roomHalfWidth) * 2
            snapped = true
            snapLine = { type: 'wall', axis: 'x', position: -roomHalfWidth }
        } else if (rightEdge > roomHalfWidth) {
            value = (roomHalfWidth - pos.x) * 2
            snapped = true
            snapLine = { type: 'wall', axis: 'x', position: roomHalfWidth }
        }

        // 他の家具へのスナップ
        if (!snapped) {
            for (const other of otherFurniture) {
                const otherRotation = other.rotation || 0
                const otherRotated90 = Math.abs(otherRotation % 180) === 90
                const otherWidth = otherRotated90 ? other.size.depth : other.size.width

                const otherLeft = other.position.x - otherWidth / 2
                const otherRight = other.position.x + otherWidth / 2

                // 右端が他の左端に近い場合
                if (Math.abs(rightEdge - otherLeft) < threshold && other.position.x > pos.x) {
                    value = (otherLeft - pos.x) * 2
                    snapped = true
                    snapLine = { type: 'furniture', axis: 'x', position: otherLeft }
                    break
                }
                // 左端が他の右端に近い場合
                if (Math.abs(leftEdge - otherRight) < threshold && other.position.x < pos.x) {
                    value = (pos.x - otherRight) * 2
                    snapped = true
                    snapLine = { type: 'furniture', axis: 'x', position: otherRight }
                    break
                }
            }
        }
    } else {
        // depth
        const frontEdge = pos.z - halfValue
        const backEdge = pos.z + halfValue

        if (frontEdge < -roomHalfDepth) {
            value = (pos.z + roomHalfDepth) * 2
            snapped = true
            snapLine = { type: 'wall', axis: 'z', position: -roomHalfDepth }
        } else if (backEdge > roomHalfDepth) {
            value = (roomHalfDepth - pos.z) * 2
            snapped = true
            snapLine = { type: 'wall', axis: 'z', position: roomHalfDepth }
        }

        // 他の家具へのスナップ
        if (!snapped) {
            for (const other of otherFurniture) {
                const otherRotation = other.rotation || 0
                const otherRotated90 = Math.abs(otherRotation % 180) === 90
                const otherDepth = otherRotated90 ? other.size.width : other.size.depth

                const otherFront = other.position.z - otherDepth / 2
                const otherBack = other.position.z + otherDepth / 2

                // 後端が他の前端に近い場合
                if (Math.abs(backEdge - otherFront) < threshold && other.position.z > pos.z) {
                    value = (otherFront - pos.z) * 2
                    snapped = true
                    snapLine = { type: 'furniture', axis: 'z', position: otherFront }
                    break
                }
                // 前端が他の後端に近い場合
                if (Math.abs(frontEdge - otherBack) < threshold && other.position.z < pos.z) {
                    value = (pos.z - otherBack) * 2
                    snapped = true
                    snapLine = { type: 'furniture', axis: 'z', position: otherBack }
                    break
                }
            }
        }
    }

    // 最小サイズ制限
    value = Math.max(20, value)

    return { value, snapped, snapLine }
}

export { POSITION_SNAP_THRESHOLD, RESIZE_SNAP_THRESHOLD }
